/**
 * <g-tabs> - Tabbed view container
 *
 * DaisyUI tablist with keyboard a11y. Each child element with a
 * `data-tab="<id>"` attribute is treated as a panel; the `tabs` JS
 * property declares the header (label + optional icon per tab). The
 * active tab is shown, others hidden.
 *
 * @example
 *   <g-tabs id="t" active="customers">
 *     <div data-tab="customers"> ...customers content... </div>
 *     <div data-tab="agents">    ...agents content...    </div>
 *   </g-tabs>
 *   <script>
 *     t.tabs = [
 *       { id: 'customers', label: 'Customers', icon: 'users' },
 *       { id: 'agents',    label: 'Agents',    icon: 'user-tie' },
 *     ];
 *     t.addEventListener('tab-change', e => console.log(e.detail.active));
 *   </script>
 *
 * Properties:
 *   .tabs   Array<{ id, label, icon? }>
 *
 * Attributes:
 *   active   tab id to show on first render (default = first tab in .tabs)
 *   variant  'box' (default) | 'border' | 'lift' — DaisyUI v5 tabs style
 *            (legacy names 'boxed', 'bordered', 'lifted' from DaisyUI v3/4
 *             are accepted and mapped to v5 equivalents for back-compat)
 *   size     'xs' | 'sm' | 'md' (default) | 'lg' | 'xl' — DaisyUI v5 tabs size
 *
 * Events:
 *   tab-change   detail: { active, previous }
 *
 * Keyboard a11y on the tablist:
 *   ArrowLeft / ArrowRight — cycle through tabs
 *   Home / End             — jump to first / last
 *   Enter / Space          — activate focused tab
 */

// DaisyUI v5 variant names. v3/v4 names ('bordered', 'boxed', 'lifted') get
// mapped to their v5 equivalents below — silently, so older specs that hard-
// code the old names still render correctly under v5.
const ALLOWED_VARIANTS = new Set(['box', 'border', 'lift']);
const VARIANT_LEGACY_MAP = { bordered: 'border', boxed: 'box', lifted: 'lift' };
const ALLOWED_SIZES = new Set(['xs', 'sm', 'md', 'lg', 'xl']);

export class GTabs extends HTMLElement {
  constructor() {
    super();
    this._tabs = [];
    this._active = null;
    this._renderScheduled = false;
  }

  static get observedAttributes() {
    return ['active', 'variant', 'size'];
  }

  attributeChangedCallback(name, _old, value) {
    if (name === 'active' && value && value !== this._active) {
      this._active = value;
    }
    this.scheduleRender();
  }

  connectedCallback() {
    this.classList.add('block', 'space-y-4');

    // Move existing children (panels) into a stable wrapper so we can
    // freely re-render the tab header above them without trashing user content.
    if (!this._panelHost) {
      this._panelHost = document.createElement('div');
      // Move every existing child INTO _panelHost
      while (this.firstChild) {
        this._panelHost.appendChild(this.firstChild);
      }
      this.appendChild(this._panelHost);
    }

    // Tab header (rebuilt on every render)
    if (!this._header) {
      this._header = document.createElement('div');
      this.insertBefore(this._header, this._panelHost);
    }

    // Pick initial active
    if (!this._active) {
      const fromAttr = this.getAttribute('active');
      this._active = fromAttr || this._firstTabId();
    }

    this.scheduleRender();
    this._header.addEventListener('keydown', this._handleKeydown);
    this._header.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    this._header?.removeEventListener('keydown', this._handleKeydown);
    this._header?.removeEventListener('click', this._handleClick);
  }

  // ── Public API ──

  get tabs() { return this._tabs; }
  set tabs(t) {
    this._tabs = Array.isArray(t) ? t : [];
    if (!this._active && this._tabs.length) this._active = this._tabs[0].id;
    this.scheduleRender();
  }

  get active() { return this._active; }
  set active(id) {
    if (id === this._active) return;
    const previous = this._active;
    this._active = id;
    this.scheduleRender();
    this.dispatchEvent(new CustomEvent('tab-change', {
      detail: { active: id, previous }, bubbles: true, composed: true,
    }));
  }

  // ── Internals ──

  _firstTabId() {
    if (this._tabs.length) return this._tabs[0].id;
    // Fallback: first [data-tab] in the panel host
    const first = this._panelHost?.querySelector('[data-tab]');
    return first?.getAttribute('data-tab') || null;
  }

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      this._render();
    });
  }

  _render() {
    if (!this._header) return;
    const rawVariant = this.getAttribute('variant');
    const mappedVariant = rawVariant && VARIANT_LEGACY_MAP[rawVariant] || rawVariant;
    const variant = ALLOWED_VARIANTS.has(mappedVariant) ? mappedVariant : 'box';
    const size = ALLOWED_SIZES.has(this.getAttribute('size')) ? this.getAttribute('size') : 'md';
    const sizeCls = size === 'md' ? '' : `tabs-${size}`;

    const tabsToRender = this._tabs.length ? this._tabs : this._inferTabsFromPanels();
    const tabButtons = tabsToRender.map(t => this._renderTabButton(t)).join('');

    // Tint the .tabs container itself (full width, primary at low opacity)
    // so the toolbar reads as a colored band regardless of variant. The
    // active-tab pill (provided by tabs-box / tabs-border / tabs-lift)
    // stands out on top of the tint.
    this._header.className = '';
    this._header.setAttribute('role', 'tablist');
    this._header.innerHTML = `<div class="tabs tabs-${variant} ${sizeCls} w-full bg-primary/10">${tabButtons}</div>`;

    // Show active panel, hide others
    const panels = this._panelHost?.querySelectorAll('[data-tab]') || [];
    panels.forEach(panel => {
      const id = panel.getAttribute('data-tab');
      const isActive = id === this._active;
      panel.style.display = isActive ? '' : 'none';
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });
  }

  _renderTabButton(tab) {
    const isActive = tab.id === this._active;
    const icon = tab.icon
      ? `<i class="fa-regular fa-${escapeHtml(tab.icon)} mr-1.5"></i>`
      : '';
    const cls = `tab ${isActive ? 'tab-active' : ''}`.trim();
    return `<button
      type="button"
      role="tab"
      data-tab-id="${escapeHtml(tab.id)}"
      class="${cls}"
      aria-selected="${isActive}"
      aria-controls="tab-panel-${escapeHtml(tab.id)}"
      tabindex="${isActive ? '0' : '-1'}"
    >${icon}${escapeHtml(tab.label || tab.id)}</button>`;
  }

  _inferTabsFromPanels() {
    const panels = this._panelHost?.querySelectorAll('[data-tab]') || [];
    return Array.from(panels).map(p => ({
      id: p.getAttribute('data-tab'),
      label: p.getAttribute('data-label') || p.getAttribute('data-tab'),
      icon: p.getAttribute('data-icon') || null,
    }));
  }

  _handleClick = (e) => {
    const btn = e.target.closest('[data-tab-id]');
    if (!btn || !this._header.contains(btn)) return;
    this.active = btn.getAttribute('data-tab-id');
  };

  _handleKeydown = (e) => {
    const list = this._tabs.length ? this._tabs : this._inferTabsFromPanels();
    if (!list.length) return;
    const ids = list.map(t => t.id);
    const idx = ids.indexOf(this._active);
    let nextIdx = null;
    if (e.key === 'ArrowRight') nextIdx = (idx + 1) % ids.length;
    else if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + ids.length) % ids.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = ids.length - 1;
    else if (e.key === 'Enter' || e.key === ' ') {
      const focused = document.activeElement?.closest('[data-tab-id]');
      if (focused) {
        e.preventDefault();
        this.active = focused.getAttribute('data-tab-id');
      }
      return;
    }
    if (nextIdx == null) return;
    e.preventDefault();
    this.active = ids[nextIdx];
    // Move focus to the newly-active tab button
    requestAnimationFrame(() => {
      const next = this._header?.querySelector(`[data-tab-id="${ids[nextIdx]}"]`);
      next?.focus();
    });
  };
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-tabs')) {
  customElements.define('g-tabs', GTabs);
}
